"use client";

import React from "react";

import * as Popover from "@radix-ui/react-popover";
import clsx from "clsx";
import { IconDropdownIndicator } from "../../icon";

type AllOptions = null;

export type MultiSelectOption<T extends string | number> = {
	label: string;
	value: T;
};

type MultiSelectProps<T extends string | number> = {
	label: string;
	value: T[] | AllOptions;
	onChange: (value: T[] | AllOptions) => void;
	options: MultiSelectOption<T>[];
	disabled?: boolean;
	className?: string;
};

export function MultiSelect<T extends string | number>({
	label,
	value,
	onChange,
	options,
	disabled = false,
	className,
}: MultiSelectProps<T>) {
	const [open, setOpen] = React.useState(false);

	const isOptionSelected = (optionValue: T) => {
		if (value === null) {
			return false;
		}
		return value.includes(optionValue);
	};

	const handleSelect = (v: T) => {
		if (value !== null && isOptionSelected(v)) {
			// Remove the item
			const selectedWithoutItem = value.filter((item) => item !== v);

			if (selectedWithoutItem.length === 0) {
				onChange(null);
				return;
			}

			onChange(selectedWithoutItem);
		} else {
			// Add the item
			const selectedWithItem = [...(value ?? []), v];
			onChange(selectedWithItem);
		}
	};

	const getDisplayText = () => {
		if (value === null) {
			return `${label}: All`;
		}
		if (value.length === 1) {
			const option = options.find((opt) => opt.value === value[0]);
			return `${label}: ${option?.label}`;
		}
		return `${label}: (${value.length})`;
	};

	return (
		<Popover.Root open={open} onOpenChange={setOpen} modal>
			<Popover.Trigger asChild>
				<button
					type="button"
					aria-expanded={open}
					className={clsx(
						"form-field inline-flex h-12 items-center gap-1 py-2 pl-4 pr-1 focus:ring-primary focus:outline-none focus:ring-1",
						disabled && "cursor-not-allowed",
						className,
					)}
				>
					<span className="text-body-sm">{getDisplayText()}</span>
					<IconDropdownIndicator
						className={clsx(
							"ml-auto text-2xl",
							disabled ? "text-neutral-400" : "text-neutral-600",
						)}
					/>
				</button>
			</Popover.Trigger>
			<Popover.Portal>
				<Popover.Content
					sideOffset={5}
					className="z-[5000] w-72 rounded-md border border-neutral-200 bg-white p-1 text-neutral-950 shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2"
				>
					<div className="space-y-1">
						<button
							type="button"
							className={clsx(
								"relative flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-neutral-100 hover:text-primary font-medium",
								value === null && "!bg-primary !text-white",
							)}
							onClick={() => {
								onChange(null);
							}}
						>
							All
						</button>
						<div className="border-b border-neutral-200" />
						{options.map((option) => (
							<button
								key={option.value}
								type="button"
								className={clsx(
									"relative flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-neutral-100 hover:text-primary",
									isOptionSelected(option.value) && "!bg-primary !text-white",
								)}
								onClick={() => handleSelect(option.value)}
							>
								{option.label}
							</button>
						))}
					</div>
				</Popover.Content>
			</Popover.Portal>
		</Popover.Root>
	);
}
